import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, CheckCircle2, WifiOff, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { allowedLocs, locOk } from "../utils/siteScope";

// Network Topology → Overview: one card per physical site combining tunnel health (/api/net/tunnel/health)
// and site-device probes (/api/net/office/status). A site is "Unreachable" when >=80% of its devices are down
// at the same time — that is a tunnel/WAN problem, not many devices failing.
const API_BASE = window.__API_BASE__ || "";
const get = async (p) => {
  const r = await fetch(API_BASE + p, { headers: { Authorization: "Bearer " + (localStorage.getItem("auth_token") || "") } });
  if (!r.ok) throw new Error(p + " → HTTP " + r.status);
  return r.json();
};
const SITES = [
  { key: "Jakarta", label: "Jakarta", companies: "Indomas", wg: "WatchGuard T80 · 10.32.0.17 (BOVPN hub)", tunnel: /jakarta/i },
  { key: "Medan", label: "Medan", companies: "PAMIN · Palmindo · Oleochem", wg: "WatchGuard Medan @ PAMIN DC · 10.21.1.4 (unconfirmed)", tunnel: /medan/i },
  { key: "Dumai", label: "Dumai", companies: "Indopalm", wg: "WatchGuard Dumai · 10.32.9.10", tunnel: /dumai/i },
  { key: "Mempawah", label: "Kijing (Mempawah)", companies: "Bio", wg: "WatchGuard Kijing", tunnel: /pbi|kijing|mempawah/i },
];
const C = { up: "#16a34a", degraded: "#d97706", down: "#dc2626", error: "#7c3aed", unknown: "#64748b", inactive: "#94a3b8" };
const card = { background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const chip = (c) => ({ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: c, background: c + "1a", border: `1px solid ${c}55`, whiteSpace: "nowrap" });
const wib = (s) => { if (!s) return "-"; const d = new Date(s.replace(" ", "T") + "Z"); return d.toLocaleString("en-GB", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); };
const ageMin = (s) => (s ? (Date.now() - new Date(s.replace(" ", "T") + "Z").getTime()) / 60000 : Infinity);

// Group simultaneous down/error events per site into incidents.
function incidents(events, devSite, siteTotal) {
  const buckets = {};
  for (const e of events) {
    if (e.status !== "down" && e.status !== "error") continue;
    const site = devSite[e.device_id]; if (!site) continue;
    const k = site + "|" + e.status + "|" + String(e.start).slice(0, 16);
    (buckets[k] = buckets[k] || { site, status: e.status, start: e.start, end: e.end, ongoing: false, n: 0 }).n++;
    const b = buckets[k];
    if (e.ongoing) b.ongoing = true;
    if (e.end && (!b.end || e.end > b.end)) b.end = e.end;
  }
  return Object.values(buckets)
    .filter((b) => siteTotal[b.site] >= 3 && b.n >= Math.ceil(siteTotal[b.site] * 0.8))
    .sort((a, b) => (a.start < b.start ? 1 : -1));
}

export default function SiteHealthOverview({ onGo }) {
  const { user } = useAuth();
  const locs = allowedLocs(user);
  const [st, setSt] = useState(null), [tn, setTn] = useState(null), [ev, setEv] = useState([]), [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [a, b, c] = await Promise.all([get("/api/net/office/status"), get("/api/net/tunnel/health?hours=24").catch(() => null), get("/api/net/office/events?days=7").catch(() => ({ events: [] }))]);
      setSt(a); setTn(b); setEv(c.events || []);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);

  if (err) return <div style={{ ...card, color: "#dc2626" }}>{err}</div>;
  if (!st) return <div style={card}>Loading site health…</div>;

  const devs = st.devices || [];
  const devSite = {}, siteTotal = {};
  devs.forEach((d) => { devSite[d.id] = d.site; siteTotal[d.site] = (siteTotal[d.site] || 0) + 1; });
  const tunnels = (tn && tn.tunnels) || [];
  const inc = incidents(ev, devSite, siteTotal);
  const labelOf = (k) => (SITES.find((s) => s.key === k) || { label: k }).label;

  const rows = SITES.filter((s) => locOk(locs, s.key)).map((s) => {
    const ds = devs.filter((d) => d.site === s.key);
    const cnt = { up: 0, degraded: 0, down: 0, error: 0 };
    ds.forEach((d) => { const x = (d.latest && d.latest.status) || "error"; cnt[x] = (cnt[x] || 0) + 1; });
    const t = tunnels.find((x) => s.tunnel.test(x.name));
    const tStat = t && t.latest ? t.latest.status : "unknown";
    const tStale = t && t.latest ? ageMin(t.latest.checked_at) > 30 : true;
    // Devices answered only through the Jakarta agent (T80 BOVPN) because the site's own PIL tunnel is down.
    const fb = ds.filter((d) => d.latest && d.latest.via === "fallback").length;
    let overall = "up", why = "All checks OK";
    if (ds.length >= 3 && cnt.down + cnt.error >= Math.ceil(ds.length * 0.8)) { overall = "down"; why = cnt.error >= cnt.down ? "Probe agent unreachable" : "Site unreachable — check tunnel / WAN"; }
    else if (fb > 0) { overall = "degraded"; why = `Direct tunnel down — site reachable via Jakarta BOVPN (${fb}/${ds.length} devices)` + (cnt.down ? ` · ${cnt.down} device(s) down` : ""); }
    else if (cnt.down > 0) { overall = "degraded"; why = `${cnt.down} device(s) down`; }
    else if (tStat === "degraded" || tStat === "down") { overall = "degraded"; why = "Tunnel " + tStat + (t.latest.detail ? " · " + t.latest.detail : ""); }
    else if (cnt.degraded > 0) { overall = "up"; why = `${cnt.degraded} device(s) with a failing service check`; }
    return { s, ds, cnt, t, tStat, tStale, overall, why, fb };
  });

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Activity size={18} color="rgb(var(--brand-600, 13 148 136))" />
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Site health</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Tunnel status + site device probes (every 5 min). A site is Unreachable when ≥80% of its devices are down together. If a site's own tunnel is down, devices are re-checked through the Jakarta BOVPN.</div>
        </div>
        <button onClick={load} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13 }}>
          <RefreshCw size={14} /> {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="sho-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12 }}>
        <style>{`@media (max-width: 1100px){ .sho-grid{ grid-template-columns: repeat(2, minmax(0,1fr)) !important; } } @media (max-width: 640px){ .sho-grid{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
        {rows.map(({ s, ds, cnt, t, tStat, tStale, overall, why, fb }) => {
          const total = ds.length || 1;
          return (
            <div key={s.key} style={{ ...card, borderTop: `4px solid ${C[overall]}`, display: "grid", gap: 8, alignContent: "start" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {overall === "down" ? <WifiOff size={16} color={C.down} /> : overall === "degraded" ? <AlertTriangle size={16} color={C.degraded} /> : <CheckCircle2 size={16} color={C.up} />}
                <span style={{ fontWeight: 700, fontSize: 15 }}>{s.label}</span>
                <span style={{ marginLeft: "auto", ...chip(C[overall]) }}>{overall === "down" ? "Unreachable" : overall === "degraded" ? "Degraded" : "OK"}</span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{s.companies}</div>
              <div style={{ fontSize: 12, color: overall === "up" ? "#475569" : C[overall] }}>{why}</div>
              <div style={{ fontSize: 11, color: "#b91c1c" }}>{s.wg}</div>
              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: "#64748b" }}>Tunnel{t ? ` · ${t.name}` : ""}</span>
                  <span style={chip(C[tStat] || C.unknown)}>{t ? tStat : "no check"}</span>
                </div>
                {t && t.latest && <div style={{ fontSize: 11, color: tStale ? C.degraded : "#94a3b8" }}>{t.latest.rtt_ms != null ? t.latest.rtt_ms + " ms · " : ""}{t.latest.ssm_status ? t.latest.ssm_status + " · " : ""}checked {wib(t.latest.checked_at)}{tStale ? " (stale)" : ""}</div>}
              </div>
              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: "#64748b" }}>Devices</span>
                  <span>{cnt.up} up · {cnt.degraded} deg · {cnt.down + cnt.error} down</span>
                </div>
                {fb > 0 && <div style={{ fontSize: 11, color: C.degraded, marginBottom: 6 }}>{fb} device(s) checked via Jakarta BOVPN (fallback)</div>}
                <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "#f1f5f9" }}>
                  {["up", "degraded", "down", "error"].map((k) => cnt[k] ? <div key={k} style={{ width: (cnt[k] / total) * 100 + "%", background: C[k] }} /> : null)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={card}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700 }}>Site incidents (7 days)</span>
          <span style={{ fontSize: 12, color: "#64748b" }}>Simultaneous outages collapsed into one row per site</span>
          {onGo && <button onClick={() => onGo("devices")} style={{ marginLeft: "auto", fontSize: 12, border: 0, background: "transparent", color: "rgb(var(--brand-700, 15 118 110))", cursor: "pointer" }}>Open device event log →</button>}
        </div>
        {inc.length === 0 ? <div style={{ fontSize: 13, color: "#64748b" }}>No site-wide outages recorded.</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ textAlign: "left", color: "#64748b" }}><th style={{ padding: 4 }}>Site</th><th style={{ padding: 4 }}>Type</th><th style={{ padding: 4 }}>Devices</th><th style={{ padding: 4 }}>Started</th><th style={{ padding: 4 }}>Recovered</th></tr></thead>
              <tbody>
                {inc.slice(0, 20).map((b, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: 4, fontWeight: 600 }}>{labelOf(b.site)}</td>
                    <td style={{ padding: 4 }}><span style={chip(b.status === "error" ? C.error : C.down)}>{b.status === "error" ? "Probe agent unreachable" : "Site unreachable"}</span></td>
                    <td style={{ padding: 4 }}>{b.n}/{siteTotal[b.site]}</td>
                    <td style={{ padding: 4 }}>{wib(b.start)}</td>
                    <td style={{ padding: 4 }}>{b.ongoing ? <span style={chip(C.down)}>ongoing</span> : wib(b.end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
